import { useState, useCallback } from "react"
import { usePage } from "@inertiajs/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { TurnstileWidget } from "./TurnstileWidget"
import { getCsrfToken } from "../utils"
import type { SharedProps } from "../types"

interface TellManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tellManagerUrl: string
  hasOwnersForFeedback: boolean
}

export function TellManagerDialog({
  open,
  onOpenChange,
  tellManagerUrl,
  hasOwnersForFeedback,
}: TellManagerDialogProps) {
  const { turnstile_site_key } = usePage().props as unknown as SharedProps

  const [contact, setContact] = useState("")
  const [message, setMessage] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  if (!hasOwnersForFeedback) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Tell Manager</DialogTitle>
            <DialogDescription>
              This feature is not available yet. The owner has not claimed
              ownership of this coffee shop.
            </DialogDescription>
          </DialogHeader>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErrors([])
    setIsSubmitting(true)

    try {
      const res = await fetch(tellManagerUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          feedback: { contact, message },
          cf_turnstile_response: turnstileToken,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        toast.success(data.message || "Your message has been sent.")
        onOpenChange(false)
        setContact("")
        setMessage("")
        setTurnstileToken("")
      } else {
        const data = await res.json()
        setErrors(data.errors || [data.error || "Something went wrong."])
      }
    } catch {
      setErrors(["Network error. Please try again."])
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Feedback & Improvement</DialogTitle>
          <DialogDescription>
            Do you have any feedback for the manager of this coffee shop? Let
            them know here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div className="rounded-md bg-red-50 p-3">
              <div className="text-sm text-red-700 space-y-1">
                {errors.map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="feedback-contact">Contact</Label>
            <Input
              id="feedback-contact"
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Email or Phone Number"
            />
            <p className="text-sm text-foreground-muted">
              Leave a way for the manager to contact you back (only if you want
              to).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message">Message</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Your message here"
              required
              rows={8}
            />
          </div>

          {turnstile_site_key && (
            <TurnstileWidget
              siteKey={turnstile_site_key}
              onVerify={handleVerify}
            />
          )}

          <DialogFooter className="flex-row sm:justify-end gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !turnstileToken}>
              {isSubmitting ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
